import TsrSvgIcon from '../../assets/icons';
import { Item } from '../interfaces/Item';
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import uniqueId from 'lodash/uniqueId';
import { SelectInputList } from './DropdownList';
import './styles.scss';
import { InputContainer } from '../InputContainer';
import { Label } from '../Label';
import { Underline } from '../Underline';
import TsrTooltip from '../../tooltip';
import { InputPropsBase, SelectPropsBase } from '../interfaces/Inputs';
import { useWindowDimension } from '../../hooks/useWindowDimension';
import { usePrevious } from '../../hooks/usePrevious';
import { isKeyAlphanumeric, KEY_ARROW_DOWN, KEY_ARROW_UP, KEY_BACKSPACE, KEY_SPACE, KEY_TAB, TextType, Variant } from '../models';
import { TooltipType } from '../models/TooltipType';
import { TextInput } from '../Text';
import { useRoveFocus } from '../../hooks';
import { DEFAULT_FOCUS_VALUE } from '../../hooks/useRoveFocus';
import { debounce } from 'lodash';

type ItemType = Item | string | number;
interface SelectInputProps extends InputPropsBase<ItemType>, SelectPropsBase {
    title?: string;
    canBeEmpty?: boolean;
    disableFocusOnClick?: boolean;
}

export type SearchQuery = {
    value: string;
    shouldSearch: boolean;
};

export type MenuVisibility = {
    show: boolean;
    showWithSelect: boolean;
};

export const SelectInput = React.forwardRef<HTMLDivElement, React.PropsWithChildren<SelectInputProps>>((props, ref) => {
    const id = uniqueId('idDropdownSelect-');
    const [isRemovable, setIsRemovable] = useState(false);
    const [currentValue, setCurrentValue] = useState(props.value);
    const [isFocused, setIsFocused] = useState(false);
    const [searchQuery, setSearchQuery] = useState<SearchQuery>({ value: '', shouldSearch: false });
    const innerRef = useRef<HTMLDivElement>(null);
    const emptyRef = useRef<HTMLDivElement>(null);
    useImperativeHandle(ref, () => innerRef.current!);
    const [width, setWidth] = useState<number | string>();
    const [windowWidth, windowHeight] = useWindowDimension();
    const [menuVisibility, setMenuVisibility] = useState<MenuVisibility>({ show: false, showWithSelect: false });
    const [isMouseOverSearchBox, setIsMouseOverSearchBox] = useState(false);
    const searchRef = useRef<HTMLInputElement>(null);
    const widthOffset = 2;
    const isStringValue = props.value && typeof props.value === 'string';
    const elementToScroll = useMemo(() => {
        return document.getElementsByClassName('dropdown-piece-selected');
    }, [menuVisibility.show]);
    const selectedItemIndex = useMemo(() => {
        if (props.options && props.value) {
            return typeof props.options[0] === 'string'
                ? props?.options.findIndex((item: ItemType) => (item as string) === props.value)
                : props.options.findIndex(
                      (item: ItemType) =>
                          (item as Item).label === (props.value as Item).label || (item as Item).label === props.value,
                  );
        } else {
            return -1;
        }
    }, [props.options, props.value]);

    const [focus, setFocus] = useRoveFocus(props.options?.length || 0, false, innerRef);

    const debouncedSubmit = useCallback(
        debounce(
            (value: ItemType, onChange?: (newValue: ItemType) => void) => onChange && onChange(value),
            props.variant === Variant.AgGrid ? 0 : 500,
        ),
        [],
    );
    useEffect(() => {
        if (menuVisibility.show) props.showSearchBar && searchRef.current?.focus();
        if (elementToScroll[0]) {
            elementToScroll[0].scrollIntoView({ block: 'center', inline: 'nearest' });
        }
    }, [menuVisibility.show]);

    useEffect(() => {
        menuVisibility.showWithSelect &&
            props.showSearchBar &&
            props.value &&
            (isStringValue ? (props.value as string) : (props.value as Item).label) &&
            searchRef.current?.select();
    }, [menuVisibility.showWithSelect]);

    useEffect(() => {
        if (innerRef.current) setWidth(innerRef.current.clientWidth);
    }, [width, innerRef.current && innerRef.current.clientWidth, windowWidth, windowHeight]);

    const onClick = () => {
        setIsRemovable(!isRemovable);
        setMenuVisibility(oldState => ({ show: !oldState.show, showWithSelect: false }));
        props.onChange && props.onChange('');
        setCurrentValue('');
    };

    const onOptionSelected = (option: ItemType) => {
        setCurrentValue(option);
        props.onChange && props.onChange(option);
    };
    useEffect(() => {
        setCurrentValue(props.value);
    }, [props.value]);
    const TitleComponent = useMemo(() => {
        if (props.title) {
            return <>{props.title}</>;
        }
        if (!props.options?.length) {
            return <>{''}</>;
        }
        if (
            typeof props.options[0] === 'string' ||
            typeof props.options[0] === 'number' ||
            typeof currentValue === 'string' ||
            typeof currentValue === 'number'
        ) {
            return <>{`${props.value}`}</>;
        }
        if (!props.value) {
            return <>{''}</>;
        }
        return (
            <>
                {(props.options &&
                    (props.options as Array<Item>).find(item => {
                        if (typeof item === 'string') {
                            return item === currentValue || props.value;
                        } else {
                            return props.value === item.label || item.id === ((props.value as Item) || currentValue).id;
                        }
                    })?.label) ||
                    ''}
            </>
        );
    }, [currentValue, props.options, props.title, props.value]);

    const previousMenuVisibility = usePrevious(menuVisibility);

    useEffect(() => {
        setFocus(selectedItemIndex);
        if (!menuVisibility.show && previousMenuVisibility?.show !== undefined) {
            props.onBlur && props.onBlur(currentValue);
        }

        !props.disableFocusOnClick && document.activeElement !== innerRef.current && setIsFocused(false);
    }, [menuVisibility.show]);
    useEffect(() => {
        props.showSearchBar && props.value
            ? setSearchQuery({
                  value: isStringValue ? (props.value as string) : (props.value as Item).label,
                  shouldSearch: false,
              })
            : setSearchQuery({ value: '', shouldSearch: false });
    }, [props.value]);

    const getMessage = useCallback(() => {
        if (props.errorMessage && !props.disabled)
            return (
                <TsrTooltip type={TooltipType.Error} icon={true} content={props.errorMessage} disabled={props.hideErrorTooltip}>
                    <TsrSvgIcon icon="information" color="error" pointer />
                </TsrTooltip>
            );
        if (props.warningMessage && !props.disabled)
            return (
                <TsrTooltip type={TooltipType.Warn} icon={true} content={props.warningMessage} disabled={props.hideErrorTooltip}>
                    <TsrSvgIcon icon="information" color="warn" pointer />
                </TsrTooltip>
            );
        if (props.infoMessage && !props.disabled)
            return (
                <TsrTooltip icon={true} content={props.infoMessage}>
                    <TsrSvgIcon icon="information" color="info" pointer />
                </TsrTooltip>
            );
        return <></>;
    }, [props.infoMessage, props.warningMessage, props.errorMessage, props.hideErrorTooltip]);

    return (
        <InputContainer
            gap={props.gap}
            justify={props.justify}
            align={props.align}
            className={`${props.className} ${props.variant}`}
            overflow={props.overflow}
            height="auto"
            width={props.width}
            inputContainerLength={props.inputContainerLength}
        >
            <Label
                id={props.id || id}
                content={props.label}
                disabled={props.disabled === true}
                labelPosition={props.labelPosition}
                labelLength={props.labelLength}
                className={props.labelClassName}
            >
                <div
                    aria-describedby={props.areaDescribedBy}
                    tabIndex={!props.disabled ? props.tabIndex || 0 : -1}
                    className={
                        props.variant !== Variant.AgGrid ? 'dropdown-list-container' : 'dropdown-list-container dlc-ag-grid'
                    }
                    ref={innerRef}
                    style={{ flex: props.inputLength || 1, width: props.inputFieldWidth }}
                    onMouseEnter={() => {
                        setIsMouseOverSearchBox(true);
                        !props.disableFocusOnClick && document.activeElement === innerRef.current && setIsFocused(true);
                    }}
                    onMouseLeave={() => {
                        setIsMouseOverSearchBox(false);
                        !props.disableFocusOnClick && document.activeElement !== innerRef.current && setIsFocused(false);
                    }}
                    onKeyDown={e => {
                        if (props.showSearchBar && isKeyAlphanumeric(e)) {
                            setFocus(DEFAULT_FOCUS_VALUE);
                            !menuVisibility.show && setMenuVisibility({ show: true, showWithSelect: false });
                            if (document.activeElement?.id === searchRef.current?.id) {
                                searchRef.current?.focus();
                            } else {
                                e.preventDefault();
                                setSearchQuery({ value: searchQuery.value + e.key, shouldSearch: true });
                                searchRef.current?.focus();
                            }
                        }
                        if (props.showSearchBar && e.key === KEY_BACKSPACE && searchQuery.value.length > 0) {
                            if (document.activeElement?.id === searchRef.current?.id) {
                            } else {
                                setSearchQuery(prev => ({ value: prev.value.slice(0, -1), shouldSearch: true }));
                            }
                        }
                        if (e.key === KEY_ARROW_DOWN) {
                            if (menuVisibility.show) {
                                if (focus === DEFAULT_FOCUS_VALUE) {
                                    setFocus(0);
                                }
                            } else {
                                if (props.options) {
                                    if (selectedItemIndex < 0) {
                                        props.onChange && props.onChange(props.options[0]);
                                        setCurrentValue(props.options[0]);
                                    } else if (selectedItemIndex >= 0) {
                                        if (selectedItemIndex === props.options.length - 1) {
                                            debouncedSubmit(props.options[0], props.onChange);
                                            setCurrentValue(props.options[0]);
                                        } else {
                                            setCurrentValue(props.options[selectedItemIndex + 1]);
                                            debouncedSubmit(props.options[selectedItemIndex + 1], props.onChange);
                                        }
                                    }
                                }
                            }
                        }
                        if (e.key === KEY_ARROW_UP) {
                            if (menuVisibility.show) {
                                if (focus === DEFAULT_FOCUS_VALUE) {
                                    setFocus(0);
                                }
                            } else {
                                if (props.options) {
                                    if (selectedItemIndex > 0) {
                                        setCurrentValue(props.options[selectedItemIndex - 1]);
                                        debouncedSubmit(props.options[selectedItemIndex - 1], props.onChange);
                                    } else if (selectedItemIndex === 0) {
                                        setCurrentValue(props.options[props.options.length - 1]);
                                        debouncedSubmit(props.options[props.options.length - 1], props.onChange);
                                    }
                                }
                            }
                        }
                        if (e.key === KEY_TAB) {
                            menuVisibility.show && setMenuVisibility({ show: false, showWithSelect: false });
                        }
                        if (e.code === KEY_SPACE) {
                            setMenuVisibility({ show: true, showWithSelect: true });
                        }
                    }}
                >
                    <div
                        className={
                            'dropdown-btn' + (props.disabled === 'inputOnly' || props.disabled === true ? ' disabled' : '')
                        }
                        onClick={() => {
                            if (!(props.disabled === 'inputOnly' || props.disabled) && !menuVisibility.show) {
                                if (isFocused || props.disableFocusOnClick) {
                                    setMenuVisibility({ show: true, showWithSelect: true });
                                } else {
                                    if (!props.disableFocusOnClick) {
                                        innerRef.current?.focus();
                                        setIsFocused(true);
                                    }
                                }
                            } else {
                                setIsFocused(false);
                            }
                        }}
                    >
                        <span
                            className={
                                props.variant === Variant.AgGrid ? 'title select-title ignoreOutsideClick' : 'title select-title'
                            }
                        >
                            {props.showSearchBar && menuVisibility.show ? (
                                <div
                                    className={props.variant === Variant.AgGrid ? 'search-box ignoreOutsideClick' : 'search-box'}
                                >
                                    <TsrSvgIcon icon="search" color="info" pointer />
                                    <TextInput
                                        id="searchId"
                                        disabled={props.disabled}
                                        onChange={value => {
                                            setSearchQuery({ value, shouldSearch: true });
                                        }}
                                        value={searchQuery.value}
                                        variant={Variant.SearchBar}
                                        gap={5}
                                        type={TextType.SearchBox}
                                        ref={searchRef}
                                        onKeyDown={e => {
                                            if (e.key === KEY_ARROW_DOWN || e.key === KEY_ARROW_UP) {
                                                const value =
                                                    typeof currentValue === ('string' || 'number')
                                                        ? (currentValue as string)
                                                        : (currentValue as Item).label;
                                                if (searchQuery.value === '' || searchQuery.value.includes(value)) {
                                                    setFocus(selectedItemIndex);
                                                } else {
                                                    setFocus(0);
                                                }
                                            } else if (e.key === KEY_TAB) {
                                                setMenuVisibility({ show: false, showWithSelect: false });
                                            }
                                        }}
                                    />
                                </div>
                            ) : (
                                <Label className="title-text d-block" id={'title'} content={TitleComponent} />
                            )}
                        </span>

                        <div className="flex">
                            {getMessage()}
                            {props.canBeEmpty && props.value && (
                                <TsrSvgIcon icon={props.value ? 'clear' : ''} color="info" pointer onClick={onClick} />
                            )}
                            <TsrSvgIcon
                                icon={menuVisibility.show ? 'arrowUp' : 'arrowDown'}
                                color="info"
                                pointer
                                onClick={() => {
                                    !props.disabled &&
                                        setMenuVisibility(oldState => ({ show: !oldState.show, showWithSelect: true }));
                                }}
                            />
                        </div>
                    </div>

                    <TsrTooltip
                        interactive
                        maxWidth={(width as number) + widthOffset}
                        appendTo={props.appendTo}
                        disabled={props.disabled === 'inputOnly' || props.disabled === true}
                        className={`${props.variant === Variant.Toolbar ? 'tippy-borderless-toolbar' : 'tippy-borderless'} ${
                            props.showSearchBar && 'tippy-with-searchBar'
                        }`}
                        onAfterUpdate={instance => {
                            !menuVisibility.show && instance.hide();
                        }}
                        onHidden={() => {
                            setMenuVisibility({ show: false, showWithSelect: false });
                            setSearchQuery({ value: '', shouldSearch: false });
                            props.value &&
                                setSearchQuery({
                                    value: props.showSearchBar
                                        ? isStringValue
                                            ? props.value.toString()
                                            : (props.value as Item).label
                                        : '',
                                    shouldSearch: false,
                                });
                        }}
                        type={TooltipType.Controls}
                        visible={menuVisibility.show}
                        onClickOutside={() => !isMouseOverSearchBox && setMenuVisibility({ show: false, showWithSelect: false })}
                        popperOptions={{ strategy: 'fixed' }}
                        content={
                            <SelectInputList
                                value={currentValue}
                                options={props.options}
                                showSearchBar={props.showSearchBar}
                                onChange={onOptionSelected}
                                onActive={value => setMenuVisibility({ show: value, showWithSelect: false })}
                                width={(width as number) + widthOffset}
                                ref={emptyRef}
                                searchQuery={searchQuery}
                                focusItem={focus}
                                setFocusItem={setFocus}
                                isMenuOpen={menuVisibility.show}
                                searchRef={searchRef}
                                refContainer={innerRef}
                                isSearchBar={props.showSearchBar}
                                variant={props.variant}
                            />
                        }
                    />
                    <Underline disabled={props.disabled === 'inputOnly' || props.disabled === true} />
                </div>
            </Label>
        </InputContainer>
    );
});
